import styled, { css } from 'styled-components';
import { BaseAvatar } from 'ui/avatar';
import { Divider } from 'ui/divider/Divider';
import { LinkButton } from 'ui/linkButton/LinkButton';
import { Link } from 'ui/links/Link';
import { ModalBody } from 'ui/modal/ModalBody';
import { PaginationFooter } from 'ui/paginationFooter/PaginationFooter';
import { Skeleton } from 'ui/skeleton/Skeleton';
import { Table } from 'ui/table/Table';
import { TableHeader } from 'ui/tableHeader/TableHeader';
import {
  Body1,
  Body2,
  body2,
  borderRadius,
  Caption,
  colors,
  fontSizes,
  fontWeights,
  H1,
  H4,
  spacing,
  Subtitle1,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

const HideScrollbar = css`
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
  -ms-overflow-style: none; /* Edge */
  scrollbar-width: none; /* Firefox */
`;

export const CriteriaTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export const CriteriaCreatePageContainer = styled.div<{ navBarHeight: number }>`
  display: flex;
  flex-direction: column;
  max-width: ${spacing.normalPageMaxWidth};
  margin: auto;
  height: calc(100vh - ${({ navBarHeight }) => `${navBarHeight}px`} - ${spacing.base});
  margin-bottom: -${spacing.doubleHalf};
`;

export const CriteriaDetailsPageContainer = styled.div`
  flex-grow: 1;
  align-self: center;
  width: 100%;
  max-width: ${spacing.normalPageMaxWidth};
`;

export const CriteriaDetailsContainer = styled.div`
  margin-top: ${spacing.double};
  display: flex;
  gap: ${spacing.doubleHalf};

  &[data-mobile-view='true'] {
    flex-direction: column-reverse;
  }
`;

export const CriteriaDetailsSidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${pxToRem(256)};

  &[data-mobile-view='true'] {
    width: 100%;
  }
`;

export const SidebarRow = styled.div`
  display: flex;
  align-items: center;

  :first-child {
    margin-bottom: ${spacing.threeQuarter};
  }

  ${H1} {
    margin-bottom: 0;
    margin-right: ${spacing.quarter};
  }
  ${Subtitle1} {
    margin-bottom: 0;
    margin-right: ${spacing.half};
  }
  ${Body2} {
    margin-left: ${spacing.quarter};
  }
`;

export const CriteriaBuilderContainer = styled.div`
  padding: ${spacing.plusHalf};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.borderPrimarySubtle};
  background: ${colors.backgroundSecondary};
  flex-grow: 1;
`;

export const SectionContent = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  ${HideScrollbar}
`;

export const SectionHeader = styled.div`
  ${H4};

  color: ${colors.textPrimary};
  font-size: ${fontSizes.s20};
  font-weight: ${fontWeights.semiBold};
  margin-top: ${spacing.x3};
  margin-bottom: ${spacing.threeQuarter};
`;

export const Section = styled.div<{ hasError?: boolean }>`
  background-color: ${colors.backgroundSecondary};
  padding: ${spacing.plusHalf};
  border-radius: ${borderRadius.xl};
  ${({ hasError }) => hasError && `border: 1px solid ${colors.borderDestructive}`};

  &:last-of-type {
    margin-bottom: ${spacing.double};
  }
`;

export const FormSection = styled(Section)`
  padding-right: 40%;
`;

export const CriteriaCreateFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 min(calc((100vw - ${spacing.normalPageMaxWidth}) / -2), -${spacing.layoutLRPadding});
  padding: ${spacing.plusHalf};
  border-top: 1px solid ${colors.borderPrimary};
  gap: ${spacing.half};
`;

export const EmptyStateBody = styled(Body1)`
  color: ${colors.textSecondary};
  max-width: ${pxToRem(461)};
`;

export const SecondaryText = styled.p`
  font-size: ${fontSizes.s16};
  text-align: center;
`;

export const StyledTable = styled(Table)`
  margin: 0 -${spacing.layoutLRPadding};
  width: 100vw;

  [role='columnheader'] {
    :first-of-type {
      padding-left: ${spacing.layoutLRPadding};
    }

    padding: ${spacing.base};
  }
`;

export const StyledTableHeader = styled(TableHeader)`
  border-top-width: ${spacing.single};
`;

export const TableHeaderLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const DetailsCellWrapper = styled.div`
  padding-left: ${spacing.plusQuarter};
`;

export const DetailsCellId = styled.div`
  white-space: nowrap;
`;

export const DetailsCellDescription = styled.div<{ $isClamped: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;

  ${({ $isClamped }) =>
    $isClamped &&
    css`
      :hover {
        text-decoration: underline;
      }
    `}
`;

export const LastModifiedCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};

  ${BaseAvatar} {
    &:after {
      content: '';
      display: contents;
    }
  }
`;

export const ActivityCellWrapper = styled.div`
  position: relative;
  display: flex;
  color: ${colors.textSecondary};
  white-space: pre;
`;

export const CriteriaName = styled(Link)`
  ${body2}
  text-decoration: none;
  border: none;
`;

export const CriteriaMetadata = styled(Caption)`
  margin-top: ${spacing.quarter};
  color: ${colors.textSecondary};
  display: flex;
`;

export const Detail = styled.div`
  display: flex;
  align-items: center;
  white-space: pre;
  &:not(:last-child):after {
    padding: 0 ${spacing.threeQuarter};
    content: '•';
    color: ${colors.textDisabled};
  }

  &:last-child {
    padding-right: 0;
  }
`;

export const PrimaryDetail = styled(Detail)`
  color: ${colors.textPrimary};
`;

export const WithBulletPoint = styled(Caption)`
  display: flex;
  align-items: center;
  color: ${colors.textSecondary};

  &:before {
    content: '\u2022';
    padding: 0 ${spacing.quarter};
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${spacing.base};
  padding: ${spacing.base} 0;
`;

export const ModalHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const StyledModalBody = styled(ModalBody)`
  white-space: pre-wrap;
`;

export const ExpandingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
`;

export const StyledPaginationFooter = styled(PaginationFooter)`
  margin: auto -${spacing.layoutLRPadding} ${spacing.plusHalf} -${spacing.layoutLRPadding};
`;

export const PageHeaderSubsection = styled.div`
  display: flex;
  justify-content: center;
  width: fit-content;
  color: ${colors.textSecondary};
`;

export const CriteriaDetailsDescription = styled(Body2)`
  overflow-wrap: break-word;
  color: ${colors.textSecondary};
`;

export const StyledDivider = styled(Divider)`
  width: 100%;
  background: ${colors.borderPrimary};
`;

export const CreatedSectionHeader = styled(Body2)`
  color: ${colors.textPrimary};
`;

export const CreatedDetails = styled(Body2)`
  color: ${colors.textPrimary};
`;

export const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StyledLinkButton = styled(LinkButton)`
  justify-content: center;
`;

export const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  top: -20px;
`;
